import { MouseEvent, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import CustomYAxisTick from '../CustomYAxisTick';
import useYAxisFormat from '../hooks/useYAxisFormat';
import CustomXAxisTick from '../CustomXAxisTick';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  DEFAULT_Y_AXIS_DOMAIN,
  HISTOGRAM_LABEL,
} from '../constants/utils';
import {
  usePrepareCohort,
  useSingleChartDataTooltip,
  useSingleDateData,
  useYAxisWidth,
} from '../hooks';
import { assertCondition } from '../../../utils/types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import useChartLines from '../hooks/useChartLines';
import {
  getFunnelDataKeysPerLine,
  MeasureMode,
} from '../FunnelChart/dataKeysUtils';
import {
  CustomFunnelLabels,
  generateBarId,
} from '../FunnelChart/CustomFunnelLabels';
import { SINGLE_DATE_DATA_KEYS } from '../hooks/useSingleDateData';
import { TooltipContextType } from '../CustomTooltip/tooltipContextInterfaces';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useXAxisSettings from '../utils/xAxis/useXAxisSettings';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import SingleChartDataTooltip from '../SingleChartDataTooltip/SingleChartDataTooltip';
import { IRechartProps } from '../hooks/tooltip/useSingleChartDataTooltip';
import { useCallback } from 'react';
import SingleDayChartWrapper from '../SingleDateChart/SingleDayChartWrapper';
import { useChartContext } from '../ChartContext';
import { YAxisPosition } from '../interfaces';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const SINGLE_LINE_KEY = 'singleLineKey';
const SINGLE_LINE_DATA_KEYS = {
  [SINGLE_LINE_KEY]: SINGLE_DATE_DATA_KEYS,
};

export default ({ analysisId, mode }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    settings: { chartWidth, chartSettings },
    metadata: { isBreakdownOverall, timeUnit },
    chartData,
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  const { chartType } = chartSettings;
  const {
    onBarMouseMove,
    onMouseLeave,
    tooltipProps,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
    isTooltipLocked,
  } = useSingleChartDataTooltip({
    containerRef,
  });
  const { handleCreateCohortFromFunnel } = usePrepareCohort({ analysisId });

  // narrow down chartData to IParsedChartDataFunnel
  assertCondition(chartData.queryType === QueryType.Funnel);
  const { data, linesMeta } = chartData;

  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const {
    singleDayLabel,
    isConversionView,
    isFunnelRelativeLabels,
    axisSettings,
  } = chartSettings;
  const dataKeysPerLine = getFunnelDataKeysPerLine(
    chartData,
    chartSettings,
    visibleLines,
    MeasureMode.VISIBLE,
  );

  const { parsedData } = useSingleDateData({
    data,
    visibleLines,
    singleDayLabel,
    linesMeta,
    ignoreOverall: false,
    chartType,
    dataKeysPerLine,
    measureControls: chartData.measureControls,
    displayedMeasures,
    queryType: QueryType.Funnel,
    timeUnit,
  });

  const currentFormat = useYAxisFormat({
    forcePercent: chartSettings.isConversionView,
  });

  const {
    percentKey,
    valueKey,
    relativeScaledBouncePercentKey,
    bouncePercentKey,
    relativeScaledBounceKey,
    bounceValueKey,
  } = SINGLE_DATE_DATA_KEYS;
  const valueDataKey = isConversionView ? percentKey : valueKey;
  const bounceDataKey = isConversionView
    ? isFunnelRelativeLabels
      ? relativeScaledBouncePercentKey
      : bouncePercentKey
    : isFunnelRelativeLabels
      ? relativeScaledBounceKey
      : bounceValueKey;

  const numItems = parsedData.length;
  const barSize = calculateBarSize(numItems, chartWidth);

  const xAxisSettings = useXAxisSettings({
    numItems,
    chartWidth,
    barSize,
    chartMode: mode,
  });

  const onBarMouseMoveHandler = useCallback(
    (
      eventProps: IRechartProps,
      _: number,
      mouseElement: MouseEvent<SVGPathElement>,
    ) => {
      if (!eventProps.name || !linesMeta[eventProps?.name]) {
        return;
      }

      const line = visibleLines.find((line) => line.name === eventProps.name);

      assertCondition(line);
      return onBarMouseMove({
        rechartProps: {
          ...eventProps,
          date: singleDayLabel,
        },
        line,
        mouseEvent: mouseElement,
      });
    },
    [linesMeta, visibleLines, onBarMouseMove, singleDayLabel],
  );

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <SingleDayChartWrapper analysisId={analysisId} ignoreCloseButton={false}>
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <BarChart
            margin={CHART_MARGIN}
            data={parsedData}
            barGap={0}
            barCategoryGap={'12%'}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              {...xAxisSettings}
              dataKey={HISTOGRAM_LABEL}
              tick={({ ...props }) => (
                <CustomXAxisTick
                  {...props}
                  barSize={barSize}
                  isAllowLabelRotated={xAxisSettings.shouldRotateXAxis}
                  isTruncateLabel={true}
                />
              )}
            />
            <YAxis
              padding={{ top: 3, bottom: 0 }}
              hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
              tickSize={0}
              domain={DEFAULT_Y_AXIS_DOMAIN}
              axisLine={true} // Whether to display the Y axis vertical line or not
              tick={
                <CustomYAxisTick
                  measureDisplayFormat={currentFormat}
                  forcePercent={isConversionView}
                />
              }
              tickCount={5}
              tickMargin={10}
              width={yAxisWidth}
            />
            <Bar
              id={generateBarId(SINGLE_LINE_KEY, false)}
              isAnimationActive={false}
              name={SINGLE_LINE_KEY}
              key={SINGLE_LINE_KEY}
              dataKey={valueDataKey}
              minPointSize={BAR_CHART_MIN_POINT_SIZE}
              strokeWidth={BAR_CHART_STROKE_WIDTH}
              barSize={barSize}
              stackId={SINGLE_LINE_KEY}
              onMouseMove={onBarMouseMoveHandler}
              onMouseLeave={onMouseLeave}
              onClick={lockTooltipPositionHandler}
            />
            <Bar
              id={generateBarId(SINGLE_LINE_KEY, true)}
              isAnimationActive={false}
              name={SINGLE_LINE_KEY}
              key={SINGLE_LINE_KEY + '-bounce'}
              dataKey={bounceDataKey}
              minPointSize={BAR_CHART_MIN_POINT_SIZE}
              strokeWidth={BAR_CHART_STROKE_WIDTH}
              barSize={barSize}
              stackId={SINGLE_LINE_KEY}
              opacity={0.12}
              onMouseMove={onBarMouseMoveHandler}
              onMouseLeave={onMouseLeave}
              onClick={lockTooltipPositionHandler}
            />
            <Customized
              key="labels"
              component={(props) => (
                <CustomFunnelLabels
                  rechartsProps={props}
                  data={parsedData}
                  dataKeysPerLine={SINGLE_LINE_DATA_KEYS}
                  chartSettings={chartSettings}
                />
              )}
            />
          </BarChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromFunnel}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
          funnelTooltipContext={{
            type: TooltipContextType.FUNNEL_CONVERSION_SINGLE,
            dataKeysPerLine,
          }}
        />
      )}
    </SingleDayChartWrapper>
  );
};
